import React, { useState, useEffect } from 'react';
import {
  Euro, User, Calculator, FileText, Check, X, Eye, ChevronDown, ChevronRight,
  Clock, AlertTriangle, CheckCircle, Download, Send, Calendar, Users,
  CreditCard, Printer, Settings, Plus, Search, Filter, GraduationCap,
  Building, ArrowLeft, FolderOpen, Briefcase, Shield, ChevronLeft, AlertCircle, Brain, Banknote, History, TrendingUp, Package,
  HandCoins, Lock, BookOpenText, Info
} from 'lucide-react';
import { api } from '../services/api';
import { Toast } from "../components/ui/Toast";
import { LongToast, shouldUseLongToast } from './ui/LongToast';
import AvancesSalaire from './AvancesSalaire';
import EtatSalaires from './EtatSalaires';
import { usePermissionsEtatSalaires } from '../hooks/usePermissionsEtatSalaires';
import GuideUtilisationDrawer from './GuideUtilisationDrawer';

// Import des sous-composants
import ComptabiliteEnseignants from './ComptabiliteEnseignants';
import ComptabilitePersonnel from './ComptabilitePersonnel';
import ComptabiliteHybrides from './ComptabiliteHybrides';

const ComptabiliteDashboard = () => {
  // ===== ÉTATS GLOBAUX =====
  // Consulter l'état des salaires est réservé (routes/etatSalaires.routes.js)
  const { peutVoir: peutVoirEtatSalaires } = usePermissionsEtatSalaires();
  const [currentView, setCurrentView] = useState('dashboard'); // 'dashboard' | 'enseignants' | 'personnel' | 'hybrides'
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [showCalendar, setShowCalendar] = useState(false);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', type: '' });

  // Données pour le dashboard
  const [enseignants, setEnseignants] = useState([]);
  const [employes, setEmployes] = useState([]);
  const [hybrides, setHybrides] = useState([]);
  const [modesPaiement, setModesPaiement] = useState([]);
  const [anneeActive, setAnneeActive] = useState(null);
  const [isGuideOpen, setIsGuideOpen] = useState(false);
  const [salaryAuthorization, setSalaryAuthorization] = useState(null);
  const [salaryParams, setSalaryParams] = useState(null);

  // ===== FONCTIONS UTILITAIRES =====

  const showToast = (message, type = 'success', options = {}) => {
    setToast({ show: true, message, type, ...options });
  };

  const hideToast = () => setToast({ show: false, message: '', type: '' });

  const formatDateForDisplay = (date) => {
    return date.toLocaleDateString('fr-FR', {
      month: 'long',
      year: 'numeric'
    });
  };

  const hasSentPayrollPointage = (line = {}) => Number(line.pointages?.nombre || 0) > 0;


  const goToPreviousMonth = () => {
    setSelectedDate(new Date(selectedDate.getFullYear(), selectedDate.getMonth() - 1, 1, 12, 0, 0));
  };

  const goToNextMonth = () => {
    setSelectedDate(new Date(selectedDate.getFullYear(), selectedDate.getMonth() + 1, 1, 12, 0, 0));
  };

  const goToCurrentMonth = () => {
    const now = new Date();
    setSelectedDate(new Date(now.getFullYear(), now.getMonth(), 1, 12, 0, 0));
  };

  // ===== USE EFFECTS =====

  useEffect(() => {
    if (toast.show) {
      const duration = shouldUseLongToast(toast.message, toast.type, toast) ? 8000 : 3000;
      const timer = setTimeout(() => {
        setToast({ show: false, message: '', type: '' });
      }, duration);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  useEffect(() => {
    loadModesPaiement();
    loadAnneeActive();
  }, []);

  useEffect(() => {
    if (currentView === 'dashboard') {
      loadDashboardData();
      return;
    }

    if (currentView === 'enseignants' || currentView === 'personnel' || currentView === 'hybrides') {
      loadSalaryAuthorization();
    }
  }, [currentView, selectedDate, anneeActive?.id]);

  // ===== FONCTIONS DE CHARGEMENT =====

  const loadSalaryAuthorization = async (yearId = null) => {
    const selectedYearId = yearId || anneeActive?.id || localStorage.getItem('annee_scolaire_id');
    if (!selectedYearId) {
      setSalaryAuthorization(null);
      setSalaryParams(null);
      setHybrides([]);
      return { authorization: null, hybrides: [] };
    }

    try {
      const etatResponse = await api.etatSalaires.getEtat({
        annee_scolaire_id: selectedYearId,
        mois: selectedDate.getMonth() + 1,
        annee: selectedDate.getFullYear()
      });
      const authorization = etatResponse.success ? (etatResponse.data?.autorisation_paiement || null) : null;
      const hybridLines = etatResponse.success
        ? (etatResponse.data?.categories?.employes_hybrides?.lignes || []).filter(hasSentPayrollPointage)
        : [];
      const params = etatResponse.success ? (etatResponse.data?.parametres_salaires || null) : null;
      setSalaryAuthorization(authorization);
      setSalaryParams(params);
      setHybrides(hybridLines);
      return { authorization, hybrides: hybridLines };
    } catch (authError) {
      console.warn('Autorisation paiement salaires non résolue:', authError.message);
      setSalaryAuthorization(null);
      setHybrides([]);
      return { authorization: null, hybrides: [] };
    }
  };

  const loadModesPaiement = async () => {
    try {
      const response = await api.paiementsSalaires.getModesPaiement();
      if (response.success) {
        setModesPaiement(response.data);
      }
    } catch (error) {
      console.error('Erreur chargement modes paiement:', error);
    }
  };

  const loadAnneeActive = async () => {
    try {
      const response = await api.anneeScolaire.getActive();
      if (response.success && response.data) {
        setAnneeActive(response.data);
        console.log('Année scolaire active:', response.data);
      }
    } catch (error) {
      console.error('Erreur chargement année active:', error);
    }
  };

  const loadDashboardData = async () => {
    try {
      const mois = (selectedDate.getMonth() + 1).toString().padStart(2, '0');
      const annee = selectedDate.getFullYear().toString();

      // Charger enseignants
      const selectedYearId = anneeActive?.id || localStorage.getItem('annee_scolaire_id');

      const salarySnapshot = await loadSalaryAuthorization(selectedYearId);
      const hybridIds = new Set((salarySnapshot?.hybrides || []).map((line) => Number(line.employe_id)).filter(Boolean));
      const respEns = await api.comptabilite.getEnseignantsAvecPointages(mois, annee, selectedYearId);
      if (respEns.success) {
        setEnseignants((respEns.data || []).filter((enseignant) => !hybridIds.has(Number(enseignant.id))));
      }

      // Charger employés
      const respEmp = await api.comptabilite.getPointagesMensuelsEmployes(
        selectedDate.getMonth() + 1,
        selectedDate.getFullYear(),
        selectedYearId
      );
      if (respEmp.success) {
        setEmployes((respEmp.data || []).filter((employe) => !hybridIds.has(Number(employe.id))));
      }
    } catch (error) {
      console.error('Erreur chargement dashboard:', error);
    }
  };



  const salaryPaymentLocked = salaryAuthorization?.paiement_autorise === false;
  const strictSalaryLockEnabled = Number(salaryParams?.verrouillage_strict_paiement_salaires || 0) === 1;
  const dashboardSalaryLocked = strictSalaryLockEnabled && salaryPaymentLocked;
  const salaryLockReasons = salaryAuthorization?.raisons_blocage || [];
  const salaryStateNotValidated = salaryAuthorization?.validation_couvre_etat !== true;
  const salaryValidationLockMessage = "Paiement verrouillé : l'état des salaires du mois n'est pas encore validé. Les paiements sont disponibles uniquement quand l'état des salaires est validé.";
  const salaryLockMessage = salaryStateNotValidated
    ? salaryValidationLockMessage
    : (salaryLockReasons[0] || salaryValidationLockMessage);

  const handleOpenSalaryModule = (view) => {
    if (dashboardSalaryLocked) {
      showToast(salaryLockMessage, 'error', { long: true, title: 'Paiement verrouillé' });
      return;
    }
    setCurrentView(view);
  };

  const DashboardSalaryLockOverlay = () => {
    if (!dashboardSalaryLocked) return null;
    return (
      <button
        type="button"
        onClick={() => showToast(salaryLockMessage, 'error', { long: true, title: 'Paiement verrouillé' })}
        className="absolute inset-0 z-20 flex cursor-not-allowed flex-col items-center justify-center bg-white/70 px-5 text-center backdrop-blur-[1px]"
      >
        <span className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-amber-100 text-amber-700 shadow-sm">
          <Lock size={23} />
        </span>
        <span className="text-sm font-semibold text-gray-900">Paiement verrouillé</span>
        <span className="mt-1 max-w-sm text-xs leading-5 text-gray-600">{salaryLockMessage}</span>
      </button>
    );
  };

  const renderGuideDrawer = () => (
    <GuideUtilisationDrawer
      isOpen={isGuideOpen}
      onClose={() => setIsGuideOpen(false)}
      module="paiementSalaires"
    />
  );

  const renderToast = () => {
    if (!toast.show) return null;
    if (shouldUseLongToast(toast.message, toast.type, toast)) {
      return (
        <LongToast
          show={toast.show}
          title={toast.title}
          message={toast.message}
          type={toast.type}
          onClose={hideToast}
        />
      );
    }
    return (
      <Toast
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={hideToast}
      />
    );
  };

  // ===== RENDU CONDITIONNEL =====

  // Vue Enseignants
  if (currentView === 'enseignants') {
    return (
      <div>
        <ComptabiliteEnseignants
          selectedDate={selectedDate}
          setSelectedDate={setSelectedDate}
          showCalendar={showCalendar}
          setShowCalendar={setShowCalendar}
          modesPaiement={modesPaiement}
          loadModesPaiement={loadModesPaiement}
          anneeActive={anneeActive}
          showToast={showToast}
          formatDateForDisplay={formatDateForDisplay}
          goToPreviousMonth={goToPreviousMonth}
          goToNextMonth={goToNextMonth}
          onBackToDashboard={() => setCurrentView('dashboard')}
          salaryAuthorization={salaryAuthorization}
        />
        {renderGuideDrawer()}
        {renderToast()}
      </div>
    );
  }

  // Vue Personnel
  if (currentView === 'personnel') {
    return (
      <div>
        <ComptabilitePersonnel
          selectedDate={selectedDate}
          setSelectedDate={setSelectedDate}
          showCalendar={showCalendar}
          setShowCalendar={setShowCalendar}
          modesPaiement={modesPaiement}
          loadModesPaiement={loadModesPaiement}
          anneeActive={anneeActive}
          showToast={showToast}
          formatDateForDisplay={formatDateForDisplay}
          goToPreviousMonth={goToPreviousMonth}
          goToNextMonth={goToNextMonth}
          onBackToDashboard={() => setCurrentView('dashboard')}
          salaryAuthorization={salaryAuthorization}
        />
        {renderGuideDrawer()}
        {renderToast()}
      </div>
    );
  }

  // Vue Employés hybrides
  if (currentView === 'hybrides') {
    return (
      <div>
        <ComptabiliteHybrides
          selectedDate={selectedDate}
          setSelectedDate={setSelectedDate}
          showCalendar={showCalendar}
          setShowCalendar={setShowCalendar}
          modesPaiement={modesPaiement}
          loadModesPaiement={loadModesPaiement}
          anneeActive={anneeActive}
          showToast={showToast}
          formatDateForDisplay={formatDateForDisplay}
          goToPreviousMonth={goToPreviousMonth}
          goToNextMonth={goToNextMonth}
          onBackToDashboard={() => setCurrentView('dashboard')}
          salaryAuthorization={salaryAuthorization}
        />
        {renderGuideDrawer()}
        {renderToast()}
      </div>
    );
  }

  // Vue Avances sur salaire
  if (currentView === 'avances') {
    return (
      <div>
        <AvancesSalaire
          selectedDate={selectedDate}
          setSelectedDate={setSelectedDate}
          anneeActive={anneeActive}
          showToast={showToast}
          formatDateForDisplay={formatDateForDisplay}
          goToPreviousMonth={goToPreviousMonth}
          goToNextMonth={goToNextMonth}
          onBackToDashboard={() => setCurrentView('dashboard')}
        />
        {renderGuideDrawer()}
        {renderToast()}
      </div>
    );
  }

  // Vue Etat des salaires
  if (currentView === 'etat-salaires' && peutVoirEtatSalaires) {
    return (
      <div>
        <EtatSalaires
          selectedDate={selectedDate}
          setSelectedDate={setSelectedDate}
          anneeActive={anneeActive}
          showToast={showToast}
          formatDateForDisplay={formatDateForDisplay}
          goToPreviousMonth={goToPreviousMonth}
          goToNextMonth={goToNextMonth}
          onBackToDashboard={() => setCurrentView('dashboard')}
        />
        {renderGuideDrawer()}
        {renderToast()}
      </div>
    );
  }
  // ===== VUE DASHBOARD (par defaut) =====
  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden">
        <div className="p-6">
          {/* En-tête */}
          <div className="mb-4 flex justify-end">
            <button
              type="button"
              onClick={() => setIsGuideOpen(true)}
              className="inline-flex items-center justify-center gap-2 rounded-lg border border-[#246990]/20 bg-[#246990]/10 px-3 py-2 text-sm font-semibold text-[#246990] transition hover:bg-[#246990]/15"
            >
              <BookOpenText size={16} />
              Guide d'utilisation
            </button>
          </div>

          <div className="mb-6 rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
            <div className="flex flex-col gap-4 rounded-lg border border-[#246990]/20 bg-[#eef7fb] p-4 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h1 className="text-xl font-bold uppercase tracking-normal text-[#246990]">MA RÉMUNÉRATION</h1>
              </div>

              <div className="text-left sm:text-right">
                <div className="text-sm text-gray-500">Période en cours</div>
                <div className="font-medium capitalize text-gray-800">{formatDateForDisplay(selectedDate)}</div>
                <div className="text-xs text-gray-500">Calculs en cours</div>
              </div>
            </div>

            <div className="mt-4 rounded-lg border border-[#246990]/15 bg-slate-50 p-4">
              <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                <div className="min-w-0">
                  <h2 className="text-base font-semibold text-[#246990]">Actions de paie</h2>
                  <p className="mt-1 text-sm text-slate-600">Traitement du mois sélectionné</p>
                </div>

                <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                  <button
                    type="button"
                    onClick={() => setCurrentView('avances')}
                    className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-lg bg-[#246990] px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-[#246990]/90"
                  >
                    <HandCoins className="h-4 w-4" />
                    Avance sur salaire
                  </button>

                  {peutVoirEtatSalaires && (
                  <button
                    type="button"
                    onClick={() => setCurrentView('etat-salaires')}
                    className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-lg bg-[#319190] px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-[#277d7c]"
                  >
                    <FileText className="h-4 w-4" />
                    État des salaires
                    <ChevronRight className="h-4 w-4" />
                  </button>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Panneau d'information : ce qu'il faut avant de payer, et pourquoi un
              employé apparaît (ou non) dans les tableaux. Purement indicatif :
              les contrôles sont faits par le serveur. */}
          <div className="mb-6 rounded-lg border border-[#246990]/20 bg-[#eef7fb] p-4">
            <div className="flex items-start gap-3">
              <Info className="mt-0.5 h-5 w-5 flex-shrink-0 text-[#246990]" />
              <div className="min-w-0 space-y-2 text-sm text-[#246990]">
                <p className="font-semibold">Avant de payer les salaires</p>
                <ul className="list-disc space-y-1 pl-5 text-slate-700">
                  <li>
                    Le paiement se prépare dans l'<strong>État des salaires</strong> : la dotation salaires doit être
                    acceptée par la caisse centrale, puis l'état validé par la comptabilité. Sans ces deux étapes, le
                    système refuse le paiement.
                  </li>
                  <li>
                    Le paiement n'est possible que pendant la période de paiement du calendrier de paie, sauf
                    ouverture exceptionnelle.
                  </li>
                  <li>
                    Un employé n'apparaît dans les tableaux <strong>Enseignants</strong>, <strong>Personnel
                    administratif</strong> et <strong>Employés hybrides</strong> que s'il a au moins un pointage du
                    mois <strong>envoyé en comptabilité</strong> depuis le module Pointage.
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Modules de paie */}
          <div className="relative">
            <div className={`grid grid-cols-1 gap-8 transition-all lg:grid-cols-3 ${dashboardSalaryLocked ? 'pointer-events-none select-none opacity-35 blur-[1px]' : ''}`}>
              {/* Module Enseignants */}
            <div
              onClick={() => handleOpenSalaryModule('enseignants')}
              className="relative overflow-hidden rounded-lg border border-gray-200 bg-white p-6 shadow-sm transition-all hover:shadow-md group"
            >
              <div className="p-4 bg-[#eef7fb] rounded-lg border border-[#246990]/20 flex items-center mb-5 group-hover:bg-[#246990] group-hover:text-white transition-colors">
                <div className="flex-1">
                  <h2 className="font-medium text-[#246990] text-xl group-hover:text-white">Enseignants</h2>
                </div>
                <ChevronRight className="text-[#246990] group-hover:text-white" size={20} />
              </div>

              <div className="space-y-4">
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <div className="bg-gray-50 px-3 py-2 border-b border-gray-200">
                    <h4 className="text-sm font-medium text-gray-700">Aperçu des enseignants</h4>
                  </div>
                  <div className="divide-y divide-gray-200">
                    {enseignants.slice(0, 3).map((teacher) => (
                      <div key={teacher.id} className="px-3 py-2 flex items-center justify-between">
                        <div className="flex items-center">
                          <div className="w-6 h-6 rounded-full bg-[#246990] text-white flex items-center justify-center mr-2">
                            <User size={12} />
                          </div>
                          <div>
                            <div className="text-sm font-medium">{teacher.prenom} {teacher.nom}</div>
                            <div className="text-xs text-gray-500">{teacher.statut}</div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="p-3 bg-[#d4a73b]/10 rounded-lg border border-[#d4a73b]/30">
                  <div className="flex items-center text-sm text-[#d4a73b]">
                    <Clock className="w-4 h-4 mr-2" />
                    <span>Calculs basés sur les pointages temps réel - retenues / avances / primes</span>
                  </div>
                </div>

              </div>
            </div>

            {/* Module Personnel Administratif */}
            <div
              onClick={() => handleOpenSalaryModule('personnel')}
              className="relative overflow-hidden rounded-lg border border-gray-200 bg-white p-6 shadow-sm transition-all hover:shadow-md group"
            >
              <div className="p-4 bg-[#eef7fb] rounded-lg border border-[#246990]/20 flex items-center mb-5 group-hover:bg-[#246990] group-hover:text-white transition-colors">
                <div className="flex-1">
                  <h2 className="font-medium text-[#246990] text-xl group-hover:text-white">Personnels administratifs</h2>
                </div>
                <ChevronRight className="text-[#246990] group-hover:text-white" size={20} />
              </div>

              <div className="space-y-4">
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <div className="bg-gray-50 px-3 py-2 border-b border-gray-200">
                    <h4 className="text-sm font-medium text-gray-700">Aperçu du personnel</h4>
                  </div>
                  <div className="divide-y divide-gray-200">
                    {employes.slice(0, 3).map((employee) => (
                      <div key={employee.id} className="px-3 py-2 flex items-center justify-between">
                        <div className="flex items-center">
                          <div className="w-6 h-6 rounded-full bg-[#246990] text-white flex items-center justify-center mr-2">
                            <User size={12} />
                          </div>
                          <div>
                            <div className="text-sm font-medium">{employee.prenom} {employee.nom}</div>
                            <div className="text-xs text-gray-500">{employee.fonction || 'Employé'}</div>
                          </div>
                        </div>
                      </div>
                    ))}
                    {employes.length === 0 && (
                      <div className="px-3 py-6 text-center text-gray-400 text-sm">
                        Aucun employé pour cette période
                      </div>
                    )}
                  </div>
                </div>

                <div className="p-3 bg-[#3084b5]/10 rounded-lg border border-[#3084b5]/30">
                  <div className="flex items-center text-sm text-[#3084b5]">
                    <Package className="w-4 h-4 mr-2" />
                    <span>Salaires fixes -retenues/-avances/+primes</span>
                  </div>
                </div>

              </div>
            </div>

            {/* Module Employes Hybrides */}
            <div
              onClick={() => handleOpenSalaryModule('hybrides')}
              className="relative overflow-hidden rounded-lg border border-gray-200 bg-white p-6 shadow-sm transition-all hover:shadow-md group"
            >
              <div className="p-4 bg-[#eef7fb] rounded-lg border border-[#246990]/20 flex items-center mb-5 group-hover:bg-[#246990] group-hover:text-white transition-colors">
                <div className="flex-1">
                  <h2 className="font-medium text-[#246990] text-xl group-hover:text-white">Employés hybrides</h2>
                </div>
                <ChevronRight className="text-[#246990] group-hover:text-white" size={20} />
              </div>

              <div className="space-y-4">
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <div className="bg-gray-50 px-3 py-2 border-b border-gray-200">
                    <h4 className="text-sm font-medium text-gray-700">Aperçu des hybrides</h4>
                  </div>
                  <div className="divide-y divide-gray-200">
                    {hybrides.slice(0, 3).map((hybride) => (
                      <div key={hybride.ligne_key || hybride.employe_id} className="px-3 py-2 flex items-center justify-between">
                        <div className="flex items-center min-w-0">
                          <div className="w-6 h-6 rounded-full bg-[#246990] text-white flex items-center justify-center mr-2 flex-shrink-0">
                            <User size={12} />
                          </div>
                          <div className="min-w-0">
                            <div className="text-sm font-medium truncate">{hybride.nom_complet || [hybride.prenom, hybride.nom].filter(Boolean).join(' ')}</div>
                            <div className="text-xs text-gray-500">Fixe + horaire</div>
                          </div>
                        </div>
                      </div>
                    ))}
                    {hybrides.length === 0 && (
                      <div className="px-3 py-6 text-center text-gray-400 text-sm">
                        Aucun employé hybride pour cette période
                      </div>
                    )}
                  </div>
                </div>

                <div className="p-3 bg-[#7c6f2f]/10 rounded-lg border border-[#7c6f2f]/30">
                  <div className="flex items-center text-sm text-[#7c6f2f]">
                    <Briefcase className="w-4 h-4 mr-2" />
                    <span>Calcul consolidé fixe + horaire, paiement dédié</span>
                  </div>
                </div>
              </div>
            </div>
            </div>
            <DashboardSalaryLockOverlay />
          </div>


        </div>

      </div>

      {renderGuideDrawer()}
      {renderToast()}
    </div>
  );
};



export default ComptabiliteDashboard;
